/* eslint-disable no-param-reassign */
import {FormControl, Stack, Typography} from '@mui/material';
import React, {Fragment, useRef} from 'react';

import {AutoSolEnum} from 'main/analytics/gaTagsEnum';
import {IconIllustration} from 'presentation/components/IconIllustration';
import {translations} from 'presentation/translations-new';
import {AttachmentInstructionsModal} from './AttachmentInstructionModal';
import {ConfirmActionModal} from './ConfirmActionModal';
import {FileUpload} from './FileUpload';
import {useFileHandler} from './hooks/useFileHandler';
import {useExternalErrorMessageStore} from './store/useExternalErrorMessageStore';
import {Container, FileInput, Label, StyledLoading} from './styles';
import {UploadFilesProps} from './types';

const {
	addAttachments,
	advice,
	warning,
	modal: {actionModal},
	fileUpload: {loadingFile},
} = translations['pt-br'].others.uploadFiles;

export function UploadFiles({
	values,
	removeFile,
	onChangeFile,
	addFilesLabel,
	isUploading,
	dataTagId,
	errorDataTagId,
}: UploadFilesProps) {
	const externalErrorState = useExternalErrorMessageStore(state => state.externalErrorMessage);
	const fileInputRef = useRef<HTMLInputElement>(null);

	const {
		errorState,
		isConfirmActionModalOpenState,
		isOnDropState,
		allowedExtensions,
		handleToggleConfirmActionModal,
		handleToggleConfirmAction,
		handleOnChange,
		handleOnDrop,
		handleDragOver,
		handleDragLeave,
		handleUploadByClick,
		handleConfirmModal,
		setIsConfirmActionModalOpenState,
		isDragAndDropState,
		setIsAttachmentInstructionModalOpenState,
		isAttachmentInstructionModalOpenState,
		setIsAttachmentInstructionCheckedState,
		isAttachmentInstructionCheckedState,
	} = useFileHandler({
		onChangeFile,
		removeFile,
		isUploading,
	});

	const hasError = externalErrorState || errorState;
	const uploadedFilesList = values?.filter(file => file.uploaded || file.error);

	return (
		<Fragment>
			{uploadedFilesList.length ? (
				<Stack gap={2}>
					{uploadedFilesList.map(file => (
						<FileUpload
							removeDataTagId={`${AutoSolEnum.CLICK_REMOVER_ANEXO_AUTOSOL}${file.id}`}
							key={file.id}
							success={file.uploaded}
							error={file.error}
							size={file.readableSize}
							title={file.filename}
							onRemove={() => {
								handleToggleConfirmActionModal();
								handleToggleConfirmAction(file.id);
							}}
						/>
					))}
				</Stack>
			) : null}

			<Container
				$isError={!!hasError}
				justifyContent="center"
				alignItems="center"
				onDrop={handleOnDrop}
				onDragOver={handleDragOver}
				onDragLeave={handleDragLeave}
				$isOnDrop={isOnDropState}
				$isUploading={isUploading}
				spacing={1}>
				{isUploading ? (
					<StyledLoading text={loadingFile} />
				) : (
					<Fragment>
						<IconIllustration
							customIconName="upload-file-filled"
							variant={hasError ? 'error' : 'primary'}
							size="custom"
						/>
						<FormControl>
							<Label
								data-tag-id={dataTagId}
								htmlFor="file"
								as="label"
								data-test="autosol-file">
								{addFilesLabel ?? addAttachments}
							</Label>
							{hasError ? (
								<Typography
									mt={1}
									variant="body2"
									color="error"
									data-tag-id={errorDataTagId}>
									{hasError}
								</Typography>
							) : null}

							<FileInput
								id="file"
								type="file"
								ref={fileInputRef}
								accept={allowedExtensions}
								onChange={e => handleOnChange(e)}
								onClick={handleUploadByClick}
							/>
						</FormControl>
					</Fragment>
				)}

				<Typography
					variant="body2"
					textAlign="center"
					color={hasError ? 'error' : 'text.secondary'}>
					{isUploading ? warning : advice}
				</Typography>
			</Container>

			<AttachmentInstructionsModal
				headerModalDataTagId={AutoSolEnum.VISUALIZOU_MODAL_DE_ANEXO_AUTOSOL}
				closeModalDataTagId={AutoSolEnum.CLICK_FECHAR_MODAL_DE_ANEXO_AUTOSOL}
				confirmModalDataTagId={AutoSolEnum.CLICK_ENTENDI_MODAL_DE_ANEXO_AUTOSOL}
				open={isAttachmentInstructionModalOpenState}
				setOpen={setIsAttachmentInstructionModalOpenState}
				fileInputRef={fileInputRef}
				isOpenedByDragAndDrop={isDragAndDropState}
				setChecked={setIsAttachmentInstructionCheckedState}
				alreadyChecked={isAttachmentInstructionCheckedState}
			/>

			<ConfirmActionModal
				modalDataTagId={AutoSolEnum.VISUALIZOU_MODAL_DE_REMOVER_ANEXO_AUTOSOL}
				closeButtonDataTagId={AutoSolEnum.CLICK_FECHAR_MODAL_DE_REMOVER_ANEXO_AUTOSOL}
				cancelButtonDataTagId={AutoSolEnum.CLICK_CANCELAR_MODAL_DE_REMOVER_ANEXO_AUTOSOL}
				confirmButtonDataTagId={AutoSolEnum.CLICK_CONFIRMAR_MODAL_DE_REMOVER_ANEXO_AUTOSOL}
				title={actionModal.title}
				description={actionModal.description}
				text={actionModal.text}
				onConfirm={handleConfirmModal}
				open={isConfirmActionModalOpenState}
				setOpen={setIsConfirmActionModalOpenState}
			/>
		</Fragment>
	);
}
